import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  TextField,
  Alert,
  Grid,
  Select as MaterialSelect,
} from '@mui/material';
import { Form, Field } from 'react-final-form';
import MenuItem from '@mui/material/MenuItem';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { Switches } from 'mui-rff';
import { useNavigate, useParams } from 'react-router-dom';
import { showSuccessMessage } from '@store/dashboard';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';
import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import { selectLoggedInUserEmail } from '@store/auth';
import { breadcrumbsActions } from '@store/breadcrumbs';
import {
  LEGACY_CONTEXTUAL_MESSAGES_LIST_ROUTE,
  addLegacyContextualMessages,
  updateLegacyContextualMessages as updateLegacyContextualMessagesBreadCrumb,
} from '../../legacy-payment-options.breadcrumbs';

import {
  createLegacyContextualMessages,
  updateLegacyContextualMessages,
  getLegacyContextualMessagesById,
  selectSelectedContextualMessage,
} from '../../store/legacy-payment-options-contextual-messages';

import {
  getCurrencies,
  selectCurrencies,
} from '../../store/legacy-payment-options-currencies';

const initForm = {
  startDateTime: dayjs(Date.now()),
  endDateTime: dayjs(Date.now()),
  transactionCurrency: '',
  locale: '',
  tiedToPromotionCode: '',
  bannerType: '',
  bannerPlacement: '',
  priority: '',
  message: '',
  translationKey: '',
  webUrl: '',
  mobUrl: '',
  keepAlways: false,
};

export default function ContextualMessagesForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formSuccessMsg, setFormSuccessMsg] = useState('');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [isFetchingData, setIsFetchingData] = useState(true);
  const currencyList = useSelector(selectCurrencies);
  const { id } = useParams();
  const [isEdit, setIsEditMode] = useState(id !== undefined);
  const selectedContextualMessage = useSelector(
    selectSelectedContextualMessage,
  );
  const userEmail = useSelector(selectLoggedInUserEmail);

  const [formData, setFormData] = useState(initForm);
  const [openDialog, setOpenDialog] = useState({ show: false });

  useEffect(() => {
    setIsEditMode(id !== undefined);
  }, [id]);

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(getCurrencies());

    // CREATE CONTEXT MESSAGE
    if (!isEdit) {
      Promise.all([
        dispatch(
          breadcrumbsActions.setBreadcrumbs(addLegacyContextualMessages),
        ),
      ]).then(() => setIsFetchingData(false));
      return;
    }

    // EDIT RULE
    dispatch(
      breadcrumbsActions.setBreadcrumbs(
        updateLegacyContextualMessagesBreadCrumb(id),
      ),
    );
    Promise.all([dispatch(getLegacyContextualMessagesById(id))]).then(() =>
      setIsFetchingData(false),
    );
  }, [dispatch, isEdit, id]);

  useEffect(() => {
    if (isEdit) {
      if (!selectedContextualMessage) return;
      const formatted = {
        ...selectedContextualMessage,
        startDateTime: dayjs(selectedContextualMessage.startDateTime),
        endDateTime: dayjs(selectedContextualMessage.endDateTime),
      };
      setFormData(formatted);
    } else {
      setFormData(initForm);
    }
  }, [id, isEdit, selectedContextualMessage]);

  const onSubmit = ({
    translationKey,
    message,
    priority,
    bannerPlacement,
    bannerType,
    webUrl,
    mobUrl,
    startDateTime,
    endDateTime,
    transactionCurrency,
    locale,
    tiedToPromotionCode,
    keepAlways,
  }) => {
    const payload = {
      translationKey,
      message,
      priority,
      bannerPlacement,
      bannerType,
      webUrl,
      mobUrl,
      startDateTime,
      endDateTime,
      transactionCurrency,
      locale,
      tiedToPromotionCode,
      keepAlways,
      user: userEmail,
    };

    setFormSuccessMsg('');
    setFormErrorMsg('');

    dispatch(
      !isEdit
        ? createLegacyContextualMessages(payload)
        : updateLegacyContextualMessages({ payload, id }),
    ).then(({ payload: response }) => {
      if (response && response.status === 'SUCCESS') {
        if (!isEdit) {
          dispatch(showSuccessMessage(`Contextual Message added successfully`));
          navigate(LEGACY_CONTEXTUAL_MESSAGES_LIST_ROUTE);
        } else {
          setFormSuccessMsg(
            'Contextual messages has been updated successfully',
          );
          setFormData(payload);
        }
      } else {
        setFormErrorMsg(
          response?.errorMessage ||
            `Unable to ${isEdit ? 'update' : 'add'} contextual message`,
        );
      }
    });
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title={`${isEdit ? 'Update' : 'New'} contextual message mapping`}
      pageReady>
      {!isFetchingData && (
        <Form
          onSubmit={onSubmit}
          initialValues={formData}
          mutators={{
            setValue: ([field, value], state, { changeValue }) => {
              changeValue(state, field, () => value);
            },
          }}
          render={({
            handleSubmit,
            pristine,
            submitting,
            // form: {
            //   mutators: { setValue },
            // },
          }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <Grid
                item
                xs={12}>
                <Field name="webUrl">
                  {({ input: { name, value, onChange } }) => (
                    <TextField
                      {...{ name, value, onChange }}
                      fullWidth
                      required
                      label="Web URL"
                      variant="outlined"
                    />
                  )}
                </Field>
              </Grid>
              <Grid
                style={{ marginTop: '1rem' }}
                item
                xs={12}>
                <Field name="mobUrl">
                  {({ input: { name, value, onChange } }) => (
                    <TextField
                      {...{ name, value, onChange }}
                      required
                      fullWidth
                      label="Mob URL"
                      variant="outlined"
                    />
                  )}
                </Field>
              </Grid>
              <Grid
                style={{ marginTop: '1rem' }}
                item
                xs={12}>
                <FormControl
                  fullWidth
                  size="medium">
                  <InputLabel id="currency-label">Currency</InputLabel>
                  <Field name="transactionCurrency">
                    {({ input: { name, value, onChange } }) => (
                      <MaterialSelect
                        required
                        labelId="currency-label"
                        {...{ name, value, onChange }}
                        label="Currency">
                        {currencyList &&
                          currencyList.map((c) => (
                            <MenuItem
                              key={c.code}
                              value={c.code}>
                              {`${c.name} (${c.code})`}
                            </MenuItem>
                          ))}
                      </MaterialSelect>
                    )}
                  </Field>
                </FormControl>
              </Grid>
              <Grid
                container
                spacing={4}>
                <Grid
                  style={{ marginTop: '1rem' }}
                  item
                  xs={12}
                  md={6}>
                  <Field name="translationKey">
                    {({ input: { name, value, onChange } }) => (
                      <TextField
                        {...{ name, value, onChange }}
                        required
                        fullWidth
                        label="Translation Key"
                        variant="outlined"
                      />
                    )}
                  </Field>
                </Grid>
                <Grid
                  style={{ marginTop: '1rem' }}
                  item
                  md={6}
                  xs={12}>
                  <Field name="locale">
                    {({ input: { name, value, onChange } }) => (
                      <TextField
                        {...{ name, value, onChange }}
                        required
                        fullWidth
                        label="Locale"
                        variant="outlined"
                      />
                    )}
                  </Field>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={4}>
                <Grid
                  style={{ marginTop: '1rem' }}
                  item
                  md={6}
                  xs={12}>
                  <Field name="message">
                    {({ input: { name, value, onChange } }) => (
                      <TextField
                        required
                        {...{ name, value, onChange }}
                        fullWidth
                        label="Message"
                        variant="outlined"
                      />
                    )}
                  </Field>
                </Grid>
                <Grid
                  style={{ marginTop: '1rem' }}
                  item
                  md={6}
                  xs={12}>
                  <Field name="priority">
                    {({ input: { name, value, onChange } }) => (
                      <TextField
                        required
                        {...{ name, value, onChange }}
                        fullWidth
                        label="Priority"
                        variant="outlined"
                      />
                    )}
                  </Field>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={4}>
                <Grid
                  style={{ marginTop: '1rem' }}
                  item
                  md={6}
                  xs={12}>
                  <Field name="bannerPlacement">
                    {({ input: { name, value, onChange } }) => (
                      <TextField
                        required
                        {...{ name, value, onChange }}
                        fullWidth
                        label="Banner Placement"
                        variant="outlined"
                      />
                    )}
                  </Field>
                </Grid>
                <Grid
                  style={{ marginTop: '1rem' }}
                  item
                  md={6}
                  xs={12}>
                  <Field name="bannerType">
                    {({ input: { name, value, onChange } }) => (
                      <TextField
                        required
                        {...{ name, value, onChange }}
                        fullWidth
                        label="Banner Type"
                        variant="outlined"
                      />
                    )}
                  </Field>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={4}>
                <Grid
                  style={{ marginTop: '1rem' }}
                  item
                  md={6}
                  xs={12}>
                  <Field name="tiedToPromotionCode">
                    {({ input: { name, value, onChange } }) => (
                      <TextField
                        required
                        {...{ name, value, onChange }}
                        fullWidth
                        label="Tied to Promotion Code"
                        variant="outlined"
                      />
                    )}
                  </Field>
                </Grid>
                <Grid
                  style={{ marginTop: '1rem' }}
                  item
                  xs={12}
                  md={6}>
                  <Field
                    name="keepAlways"
                    type="checkbox">
                    {({ input: { name, value, onChange } }) => (
                      <Switches
                        {...{ name, value, onChange }}
                        data={{
                          label: 'Keep Always',
                        }}
                      />
                    )}
                  </Field>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={4}>
                <Grid
                  style={{ marginTop: '1rem' }}
                  item
                  xs={12}
                  md={6}>
                  <Field name="startDateTime">
                    {({
                      input: { name, value, onChange },
                      meta: { error },
                    }) => (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          required
                          {...{ name, value }}
                          onChange={(newValue) => {
                            if (!newValue) return;
                            onChange(newValue);
                          }}
                          label="Start Date Time"
                          slotProps={{
                            field: { fullWidth: true },
                            textField: {
                              helperText: error,
                              error: !!error,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    )}
                  </Field>
                </Grid>
                <Grid
                  style={{ marginTop: '1rem' }}
                  item
                  xs={12}
                  md={6}>
                  <Field name="endDateTime">
                    {({
                      input: { name, value, onChange },
                      meta: { error },
                    }) => (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          required
                          {...{ name, value }}
                          onChange={(newValue) => {
                            if (!newValue) return;
                            onChange(newValue);
                          }}
                          label="End Date Time"
                          slotProps={{
                            field: { fullWidth: true },
                            textField: {
                              helperText: error,
                              error: !!error,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    )}
                  </Field>
                </Grid>
              </Grid>

              {formSuccessMsg && (
                <Alert
                  onClose={() => {
                    setFormSuccessMsg('');
                  }}
                  severity="success"
                  sx={{ mt: 2 }}>
                  {formSuccessMsg}
                </Alert>
              )}
              {formErrorMsg && (
                <Alert
                  onClose={() => {
                    setFormErrorMsg('');
                  }}
                  severity="error"
                  sx={{ mt: 2 }}>
                  {formErrorMsg}
                </Alert>
              )}

              <Button
                id="submitBtn"
                style={{ marginTop: '32px' }}
                fullWidth
                size="large"
                variant="contained"
                type="submit"
                disabled={pristine || submitting}>
                Submit
              </Button>
            </form>
          )}
        />
      )}
    </ApplicationPageTemplate>
  );
}
